import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export type ComboboxMultiSelectCountOption<T extends string = string> = {
  value: T;
  label: string;
};

export type ComboboxMultiSelectCountProps<T extends string = string> = {
  options: ComboboxMultiSelectCountOption<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  placeholder: string;
  formatSelectionSummary: (count: number) => string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  'aria-label'?: string;
};

export function ComboboxMultiSelectCount<T extends string = string>({
  options,
  value,
  onChange,
  placeholder,
  formatSelectionSummary,
  searchPlaceholder,
  emptyText,
  className,
  triggerClassName,
  contentClassName,
  'aria-label': ariaLabel,
}: ComboboxMultiSelectCountProps<T>) {
  const [open, setOpen] = useState(false);

  const toggle = (item: T) => {
    onChange(
      value.includes(item)
        ? value.filter((v) => v !== item)
        : [...value, item]
    );
  };

  return (
    <div className={cn('w-full min-w-0', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            className={cn(
              'h-8 min-h-8 w-full min-w-0 justify-between font-normal',
              triggerClassName
            )}
          >
            <span className="min-w-0 flex-1 truncate text-left">
              {value.length > 0
                ? formatSelectionSummary(value.length)
                : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn(
            'p-0 w-[var(--radix-popover-trigger-width)] min-w-[12rem] max-w-sm',
            contentClassName
          )}
        >
          <Command>
            {/* <CommandInput placeholder={searchPlaceholder ?? ''} /> */}
            <CommandList>
              <CommandEmpty>{emptyText ?? '-'}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.value}`}
                    onSelect={() => toggle(option.value)}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        value.includes(option.value)
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
